import { Eye, EyeOff } from 'lucide-react'
import { motion } from 'motion/react'
import * as React from 'react'

import { usePressMotion } from '../lib/press-motion'
import { TextField, type TextFieldProps } from './text-field'

export interface PasswordFieldProps extends Omit<TextFieldProps, 'type' | 'rightIcon'> {
  /** aria-label for the reveal button while the password is hidden. */
  showLabel?: string
  /** aria-label for the reveal button while the password is visible. */
  hideLabel?: string
}

// A TextField that masks its value and adds a show/hide reveal toggle in the trailing
// slot. The visibility state is owned here, so callers just render <PasswordField /> and
// pass the usual field props through to TextField. Labels are props (with English
// defaults) rather than translated internally, keeping this package i18n-agnostic.
const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ showLabel = 'Show password', hideLabel = 'Hide password', disabled, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false)
    const press = usePressMotion()
    return (
      <TextField
        ref={ref}
        type={visible ? 'text' : 'password'}
        disabled={disabled}
        {...props}
        rightIcon={
          <motion.button
            type="button"
            {...press}
            onClick={() => setVisible((v) => !v)}
            disabled={disabled}
            aria-label={visible ? hideLabel : showLabel}
            aria-pressed={visible}
            // Focus ring is the app-wide pelorous pairing, but flush — no ring-offset, same as
            // SearchField's clear button. The offset separates a ring from the surface a control
            // sits on; inside a field there's no such gap to carve.
            className="-mr-1.5 flex size-[34px] items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card-sunken hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 disabled:cursor-not-allowed dark:focus-visible:ring-pelorous-300">
            {visible ? <EyeOff /> : <Eye />}
          </motion.button>
        }
      />
    )
  }
)
PasswordField.displayName = 'PasswordField'

export { PasswordField }
